import { useEffect, useState } from "react";
import { imageUrl } from "../lib/api";
import { PhotoPicker } from "../components/PhotoPicker";
import { useI18n } from "../i18n/I18nProvider";
import { useMoney } from "../lib/CurrencyProvider";

interface IdentifyResult {
  id: number;
  sku: string;
  slug: string;
  name: { es: string; en: string };
  setName: string | null;
  cardNumber: string | null;
  rarity: string | null;
  category: string;
  imageKey: string;
  price: number;
  stock: number;
}

const ERRORS: Record<string, string> = {
  missing_image: "No se recibió la foto",
  image_too_large: "La foto es muy grande (máx 10 MB)",
  ocr_failed: "No pudimos leer el nombre de la carta. Probá con mejor luz, más cerca y sin reflejos.",
  unsupported_language: "Por ahora solo reconocemos cartas en español o inglés.",
  unsupported_image: "No se pudo abrir la foto. Probá con otra.",
};

interface Photo {
  blob: Blob;
  url: string;
}

export function IdentifyPage() {
  const { t } = useI18n();
  const { format } = useMoney();
  const [photo, setPhotoState] = useState<Photo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<IdentifyResult[] | null>(null);
  const [rawOcr, setRawOcr] = useState<string | null>(null);

  // One object URL alive at a time: revoke the previous preview when it changes or on unmount.
  useEffect(() => {
    if (!photo) return;
    return () => URL.revokeObjectURL(photo.url);
  }, [photo]);

  const setPhoto = (blob: Blob | null) => setPhotoState(blob ? { blob, url: URL.createObjectURL(blob) } : null);

  function retake() {
    setPhoto(null);
    setResults(null);
    setError(null);
    setRawOcr(null);
  }

  async function identify() {
    if (!photo) return;
    setLoading(true);
    setError(null);
    setResults(null);
    setRawOcr(null);
    try {
      const form = new FormData();
      form.append("image", photo.blob, "card.jpg");
      const res = await fetch("/api/cards/identify", { method: "POST", body: form });
      const json = await res.json();
      if (!json.success) {
        setError(ERRORS[json.error.code] ?? json.error.message);
        return;
      }
      setResults(json.data.results);
      setRawOcr(json.data.text);
      if (json.data.results.length === 0)
        setError("No se encontró ninguna carta con ese código en el catálogo.");
    } catch {
      setError("Error de conexión. Intentá de nuevo.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container section identify-page">
      <h1>{t("identificar_titulo")}</h1>
      <p className="muted">{t("identificar_desc")}</p>

      {!photo ? (
        <div className="identify-camera">
          <PhotoPicker
            onPhoto={(blob) => {
              setError(null);
              setPhoto(blob);
            }}
            onError={setError}
            labels={{ camera: t("abrir_camara"), gallery: t("subir_foto"), shoot: t("sacar_foto"), cancel: t("cancelar"), starting: t("buscando") }}
          />
        </div>
      ) : (
        <div className="identify-review">
          <img src={photo.url} alt="Foto de la carta" className="identify-preview" />
          <div className="identify-review-actions">
            <button className="btn btn-primary" onClick={identify} disabled={loading}>
              {loading ? t("buscando") : "🔍 " + t("identificar_accion")}
            </button>
            <button className="btn" onClick={retake}>
              {t("tomar_otra")}
            </button>
          </div>
        </div>
      )}

      {error && <div className="identify-error">{error}</div>}
      {loading && <div className="identify-loading">{t("procesando")}</div>}

      {results && results.length > 0 && (
        <div className="identify-results">
          <h2>{t("resultados")}</h2>
          {rawOcr && (
            <details className="identify-ocr-debug">
              <summary>OCR</summary>
              <pre>{rawOcr}</pre>
            </details>
          )}
          <div className="identify-grid">
            {results.map((card: IdentifyResult) => (
              <a key={card.id} href={`/producto/${card.slug}`} className="identify-card">
                <img src={imageUrl(card.imageKey)} alt={card.name.es} className="identify-card-img" loading="lazy" />
                <div className="identify-card-info">
                  <strong>{card.name.es}</strong>
                  <span className="muted">
                    {[card.setName, card.cardNumber, card.rarity].filter(Boolean).join(" · ")}
                  </span>
                  <span className="identify-card-price">
                    {format(card.price)}
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}